import { ReportDomain } from '@mode/shared/contract-common';
import { createReducer, on } from '@ngrx/store';
import { ReportResource } from 'libs/shared/data-access-webapp/src/lib/reports/report-response.types';
import * as ReportActions from './report.actions';

export const reportFeatureKey = 'report';

export interface State {
  report: ReportDomain.Report | null;
  headerReportData: ReportResource | null;
  headerReportDataIsLoading: boolean;
  headerReportDataIsComplete: boolean;
  headerReportDataError: Error | null;
  reportIsLoading: boolean;
  reportIsComplete: boolean;
  reportError: Error | null;
}

export const initialState: State = {
  report: null,
  headerReportData: null,
  headerReportDataIsLoading: false,
  headerReportDataIsComplete: false,
  headerReportDataError: null,
  reportIsComplete: false,
  reportIsLoading: false,
  reportError: null,
};

export const reducer = createReducer(
  initialState,
  on(ReportActions.editorRouteLoadReport, (state) => ({
    ...state,
    reportIsComplete: false,
    reportIsLoading: true,
    reportError: null,
  })),
  on(ReportActions.datasetViewRouteLoadReport, (state) => ({
    ...state,
    reportIsComplete: false,
    reportIsLoading: true,
    reportError: null,
  })),
  on(ReportActions.apiLoadReportSuccess, (state, action) => ({
    ...state,
    report: action.report,
    reportIsLoading: false,
    reportIsComplete: true,
    reportError: null,
  })),
  on(ReportActions.apiLoadReportFailure, (state, action) => ({
    ...state,
    reportIsComplete: true,
    reportIsLoading: false,
    reportError: action.error,
  })),
  on(ReportActions.apiRenameReportSuccess, (state, action) => {
    return {
      ...state,
      report: action.report,
      headerReportData: <ReportResource>{
        ...state.headerReportData,
        name: action.report.name,
      },
    };
  }),
  on(ReportActions.publishDatasetSuccess, (state, action) => {
    return {
      ...state,
      headerReportData: <ReportResource>{
        ...state.headerReportData,
        name: action.name,
        thoughtspot_published_at: action.publishedAt,
      },
    };
  }),
  on(ReportActions.loadWorkspaceHeaderData, (state) => ({
    ...state,
    headerReportDataIsLoading: true,
    headerReportDataIsComplete: false,
    headerReportDataError: null,
  })),
  on(ReportActions.loadWorkspaceHeaderDataSuccess, (state, action) => ({
    ...state,
    headerReportData: { ...state.headerReportData, ...action.headerReportData },
    headerReportDataIsLoading: false,
    headerReportDataIsComplete: true,
    headerReportDataError: null,
  })),
  on(ReportActions.loadWorkspaceHeaderDataFailure, (state, action) => ({
    ...state,
    headerReportDataIsLoading: false,
    headerReportDataIsComplete: true,
    headerReportDataError: action.error,
  })),
  on(ReportActions.loadReportViewContainerHeaderData, (state) => ({
    ...state,
    headerReportDataIsLoading: true,
    headerReportDataIsComplete: false,
    headerReportDataError: null,
  })),
  on(ReportActions.loadReportViewContainerHeaderDataSuccess, (state, action) => ({
    ...state,
    headerReportData: { ...state.headerReportData, ...action.headerReportData },
    headerReportDataIsLoading: false,
    headerReportDataIsComplete: true,
    headerReportDataError: null,
  })),
  on(ReportActions.loadReportViewContainerHeaderDataFailure, (state, action) => ({
    ...state,
    headerReportDataIsLoading: false,
    headerReportDataIsComplete: true,
    headerReportDataError: action.error,
  })),
  on(ReportActions.updateHeaderReportDataManually, (state, action) => {
    const embeds = state.headerReportData?._embedded;
    const forms = state.headerReportData?._forms;
    const links = state.headerReportData?._links;
    return {
      ...state,
      headerReportData: {
        ...(state.headerReportData as ReportResource),
        ...action.headerReportData,
        _embedded: { ...embeds, ...action.headerReportData._embedded },
        _forms: { ...forms, ...action.headerReportData._forms },
        _links: { ...links, ...action.headerReportData._links },
      },
      headerReportDataIsLoading: false,
      headerReportDataIsComplete: true,
      headerReportDataError: null,
    };
  }),
  on(ReportActions.updateHeaderSpaceDataManually, (state, action) => {
    const embeds = state.headerReportData?._embedded;
    return {
      ...state,
      headerReportData: {
        ...(state.headerReportData as ReportResource),
        _embedded: {
          ...embeds,
          space: {
            ...embeds?.['space'],
            ...action.space,
          } as any,
        },
      },
      headerReportDataIsLoading: false,
      headerReportDataIsComplete: true,
      headerReportDataError: null,
    };
  }),
  on(ReportActions.loadDatasetWorkspaceHeaderData, (state) => ({
    ...state,
    headerReportDataIsLoading: true,
    headerReportDataIsComplete: false,
    headerReportDataError: null,
  })),
  on(ReportActions.loadDatasetWorkspaceHeaderDataSuccess, (state, action) => ({
    ...state,
    headerReportData: { ...state.headerReportData, ...action.headerReportData },
    headerReportDataIsLoading: false,
    headerReportDataIsComplete: true,
    headerReportDataError: null,
  })),
  on(ReportActions.loadDatasetWorkspaceHeaderDataFailure, (state, action) => ({
    ...state,
    headerReportDataIsLoading: false,
    headerReportDataIsComplete: true,
    headerReportDataError: action.error,
  })),
  on(ReportActions.updateHeaderReportSharedState, (state, action) => {
    const forms = state.headerReportData?._forms;
    return {
      ...state,
      headerReportData: <ReportResource>{
        ...state.headerReportData,
        shared: action.shared,
        _forms: { ...forms, ...action.forms },
      },
      headerReportDataIsLoading: false,
      headerReportDataIsComplete: true,
      headerReportDataError: null,
    };
  }),
  on(ReportActions.loadRestrictedReportViewContainerHeaderData, (state) => ({
    ...state,
    headerReportDataIsLoading: true,
    headerReportDataIsComplete: false,
    headerReportDataError: null,
  })),
  on(ReportActions.loadRestrictedReportViewContainerHeaderDataSuccess, (state, action) => ({
    ...state,
    headerReportData: { ...state.headerReportData, ...action.headerReportData },
    headerReportDataIsLoading: false,
    headerReportDataIsComplete: true,
    headerReportDataError: null,
  })),
  on(ReportActions.loadRestrictedReportViewContainerHeaderDataFailure, (state, action) => ({
    ...state,
    headerReportDataIsLoading: false,
    headerReportDataIsComplete: true,
    headerReportDataError: action.error,
  }))
);
